import type React from 'react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarX, MapPin, Radio, Tent } from 'lucide-react';
import publicFestivalApi from '@showliner/shared/services/publicFestivalApi';
import {
  festivalDatesLabel,
  festivalDays,
  liveStages,
  rangeLabel,
  type PublicFestival,
  type PublicFestivalStage,
} from '@showliner/shared/utils/festivalPublic';
import { buildJoinUrl } from '@showliner/shared/utils/joinUrl';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { getAssetUrl, getLiveUrl } from '@/utils/crossAppLinks';
import { usePageTitle } from '@/hooks/usePageTitle';

/**
 * A festival's public page (docs/plans/FESTIVALS.md Phase 6): what's on now,
 * with a Join button per live stage, then every day's stages with their acts.
 * Set times appear once the organizer publishes the schedule; until then the
 * acts are in the bill's order. Only revealed acts are ever in the payload, so
 * nothing here has to hide anything. A private festival has no page (404).
 */

const Stage: React.FC<{ stage: PublicFestivalStage; festival: PublicFestival }> = ({ stage, festival }) => {
  const hours = rangeLabel(stage.startsAt, stage.endsAt, festival);
  return (
    <div className="w-full rounded-lg border border-border/50 overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-muted/30 border-b border-border/50">
        {stage.venue?.profilePicture ? (
          <Avatar className="h-9 w-9 rounded-md">
            <AvatarImage src={getAssetUrl(stage.venue.profilePicture)} alt="" />
            <AvatarFallback className="rounded-md">{stage.label.charAt(0)}</AvatarFallback>
          </Avatar>
        ) : null}
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-grey-900 truncate">{stage.label}</h3>
          <p className="text-xs text-muted-foreground truncate">
            {hours}
            {stage.venue && stage.venue.name !== stage.label && (
              <>
                {hours && <span> · </span>}
                <Link to={`/venue/${stage.venue.id}`} className="hover:text-foreground">{stage.venue.name}</Link>
              </>
            )}
          </p>
        </div>
        {stage.live && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
            <Radio className="h-3.5 w-3.5" /> Live
          </span>
        )}
        {stage.played && <span className="text-xs text-muted-foreground">Played</span>}
      </div>
      {stage.acts.length === 0 ? (
        <p className="px-4 py-3 text-sm text-muted-foreground">Lineup coming soon</p>
      ) : (
        <div className="divide-y divide-border/30">
          {stage.acts.map((act) => {
            const set = rangeLabel(act.setStartTime, act.setEndTime, festival);
            return (
              <Link
                key={act.performerId}
                to={`/performer/${act.performerId}`}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/20 transition-colors"
              >
                <Avatar className="h-9 w-9">
                  {act.profilePicture ? <AvatarImage src={getAssetUrl(act.profilePicture)} alt="" /> : null}
                  <AvatarFallback className="text-sm font-semibold bg-primary text-white">
                    {act.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="flex-1 min-w-0 text-sm font-medium text-grey-900 truncate">{act.name}</span>
                {set && <span className="flex-shrink-0 text-xs text-muted-foreground tabular-nums">{set}</span>}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FestivalPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [festival, setFestival] = useState<PublicFestival | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');

  usePageTitle(festival?.name);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setState('loading');
    publicFestivalApi.get(id)
      .then((data) => { if (!cancelled) { setFestival(data); setState('ready'); } })
      .catch((err) => {
        if (cancelled) return;
        setState(err?.response?.status === 404 ? 'missing' : 'error');
      });
    return () => { cancelled = true; };
  }, [id]);

  if (state === 'loading') {
    return (
      <div className="flex flex-col px-4 pt-12 pb-8 max-w-lg mx-auto gap-3">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-4 w-32" />
        {[1, 2].map((i) => <Skeleton key={i} className="h-32 w-full rounded-lg" />)}
      </div>
    );
  }

  if (state !== 'ready' || !festival) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4 text-center">
        <Tent className="h-16 w-16 text-muted-foreground/50" />
        <p className="text-lg text-muted-foreground">
          {state === 'missing' ? 'This festival isn\'t on Showliner.' : 'Couldn\'t load this festival. Try again.'}
        </p>
        <Button variant="outline" asChild>
          <Link to="/discover">Discover shows</Link>
        </Button>
      </div>
    );
  }

  const live = liveStages(festival);
  const days = festivalDays(festival);
  const dates = festivalDatesLabel(festival);

  return (
    <div className="flex flex-col px-4 pt-12 pb-16 max-w-lg mx-auto">
      <h1 className="font-display text-2xl font-bold tracking-tight text-grey-900">{festival.name}</h1>
      {dates && <p className="text-sm text-muted-foreground mt-1">{dates}</p>}

      {festival.cancelled && (
        <div className="mt-6 flex items-start gap-3 rounded-lg border border-border/50 bg-muted/30 px-4 py-3">
          <CalendarX className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
          <p className="text-sm text-grey-900">This festival was cancelled.</p>
        </div>
      )}

      {live.length > 0 && (
        <section className="mt-8" aria-labelledby="on-now">
          <h2 id="on-now" className="text-base font-semibold text-grey-900">On now</h2>
          <div className="mt-3 space-y-2">
            {live.map((stage) => (
              <div key={stage.id} className="flex items-center gap-3 rounded-lg border border-border/50 px-4 py-3">
                <Radio className="h-4 w-4 flex-shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-grey-900 truncate">{stage.label}</p>
                  {stage.venue && stage.venue.name !== stage.label && (
                    <p className="text-xs text-muted-foreground truncate flex items-center gap-0.5">
                      <MapPin className="h-3 w-3" /> {stage.venue.name}
                    </p>
                  )}
                </div>
                <Button size="sm" asChild>
                  <a href={buildJoinUrl(getLiveUrl(), stage.roomCode!)}>Join</a>
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      {days.length === 0 ? (
        <p className="mt-8 text-sm text-muted-foreground">The stages haven't been announced yet.</p>
      ) : days.map((day) => (
        <section key={day.key} className="mt-8" aria-label={day.label}>
          <h2 className="text-base font-semibold text-grey-900">{day.label}</h2>
          <div className="mt-3 space-y-3">
            {day.stages.map((stage) => <Stage key={stage.id} stage={stage} festival={festival} />)}
          </div>
        </section>
      ))}

      {!festival.schedulePublished && days.length > 0 && !festival.cancelled && (
        <p className="mt-6 text-xs text-muted-foreground">Set times will be posted closer to the day.</p>
      )}
    </div>
  );
};

export default FestivalPage;
